import React, { useState, useEffect, useRef, useCallback } from 'react';
import { message } from 'antd';
import { withApollo } from 'react-apollo';
import { withGetCompany } from 'src/_shared/api/components/settings/';
import { connect } from 'react-redux';
import { userActions } from 'src/actions.js';
import MilestoneSettingsCard from './MilestoneSettingsCard.jsx';
import MilestoneRewardCard from './MilestoneRewardCard.jsx';
import MilestoneBadgesCard from './MilestoneBadgesCard.jsx';
import { motion } from 'motion/react';

const ManageMilestonesContainer = ({
	currentUser,
	onUpdateCompany,
	updateCurrentUserCompany,
}) => {
	const recognitionSettings = currentUser?.company?.recognitionSettings || {};
	const [milestoneColorMap, setMilestoneColorMap] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [milestoneRewards, setMilestoneRewards] = useState([]);
	const [milestoneBadges, setMilestoneBadges] = useState([]);
	const badgesRefreshRef = useRef(null);

	const handleRewardsLoaded = useCallback((rewards) => {
		setMilestoneRewards(rewards);
	}, []);

	const handleBadgesLoaded = useCallback((badges) => {
		setMilestoneBadges(badges);
	}, []);

	const refreshBadges = useCallback(() => {
		if (badgesRefreshRef.current) {
			badgesRefreshRef.current();
		}
	}, []);

	useEffect(() => {
		const existing = recognitionSettings?.milestoneColorMap;
		if (existing && existing.length > 0) {
			setMilestoneColorMap(existing);
		} else {
			setMilestoneColorMap([{ years: 0, color: null }]);
		}
	}, [recognitionSettings?.milestoneColorMap]);

	const handleSaveSettings = async ({
		milestoneColorMap: updatedMap,
		milestoneClaimExpiryDays,
		milestoneClaimModalTitle,
		milestoneClaimModalMessage,
	}) => {
		setIsSaving(true);
		try {
			const currentSettings = { ...recognitionSettings };

			if (
				currentSettings.tieredRewardsCampaign &&
				Array.isArray(currentSettings.tieredRewardsCampaign)
			) {
				currentSettings.tieredRewardsCampaign =
					currentSettings.tieredRewardsCampaign.map((tier) => {
						const { snappyGrant, ...cleanTier } = tier;
						return cleanTier;
					});
			}

			const result = await onUpdateCompany({
				input: {
					id: currentUser?.company?.id,
					recognitionSettings: {
						...currentSettings,
						milestoneColorMap: updatedMap,
						milestoneClaimExpiryDays,
						milestoneClaimModalTitle,
						milestoneClaimModalMessage,
					},
				},
			});

			if (result?.data?.updateCompany) {
				updateCurrentUserCompany(result?.data?.updateCompany);
				setMilestoneColorMap(updatedMap);
				message.success('Milestone settings saved successfully!');
			}
		} catch (error) {
			console.error(error);
			message.error('Something went wrong, please try again later');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
			<motion.h1
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				style={{
					fontSize: '24px',
					fontWeight: '700',
					marginBottom: '24px',
					color: '#1a1a2e',
				}}
			>
				Manage Milestones
			</motion.h1>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.1 }}
			>
				<MilestoneBadgesCard
					currentUser={currentUser}
					milestoneRewards={milestoneRewards}
					refreshRef={badgesRefreshRef}
					onBadgesLoaded={handleBadgesLoaded}
				/>
			</motion.div>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.2 }}
			>
				<MilestoneSettingsCard
					milestoneColorMap={milestoneColorMap}
					recognitionSettings={recognitionSettings}
					onSave={handleSaveSettings}
					isSaving={isSaving}
				/>
			</motion.div>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.3 }}
			>
				<MilestoneRewardCard
					currentUser={currentUser}
					onRewardsLoaded={handleRewardsLoaded}
					milestoneBadges={milestoneBadges}
					refreshBadges={refreshBadges}
				/>
			</motion.div>
		</div>
	);
};

const mapDispatchToProperties = (dispatch) => {
	return {
		updateCurrentUserCompany(company) {
			dispatch(userActions.updateUserCompany(company));
		},
	};
};

export default withGetCompany(
	connect(null, mapDispatchToProperties)(withApollo(ManageMilestonesContainer))
);
